import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CellRoot,
  TicketLine,
  StatusChip,
  ProductName,
  MetaText,
  PeriodText,
  PeriodGroup,
  DdayText,
  StoppedNote,
  ExpiredToggle,
  ToggleChevron,
} from './TicketInfoCellStyled';

// 상태(labelStatus) → 칩 라벨/글자색. 서버가 판정한 값을 매핑만 한다(아웃라인 칩, 상태만 노출).
const STATUS_CHIP_MAP = {
  ACTIVE: { label: '활성', fg: '#00838F' },
  UPCOMING: { label: '예정', fg: '#3564D6' },
  EXPIRING: { label: '임박', fg: '#C46A00' },
  STOPPED: { label: '중지', fg: '#C2403D' },
  EXPIRED: { label: '만료', fg: '#8A939C' },
};

const formatPeriod = (startDate, endDate) => {
  if (startDate && endDate) return `${startDate} ~ ${endDate}`;
  if (startDate) return startDate;
  if (endDate) return endDate;
  return '';
};

// 상태 무관, 셀에 기본 노출할 최대 줄 수. 초과분과 만료(EXPIRED)는 접어서
// 수강권이 많은 회원의 행이 과도하게 길어지지 않게 한다.
const MAX_VISIBLE_TICKETS = 3;

// 노출 정렬 우선순위: 활성 > 사용예정 > 만료임박 > 중지 (만료는 별도로 항상 뒤).
// 값이 작을수록 앞. 목록에서 활성 상품이 먼저 보이도록 한다.
const STATUS_PRIORITY = { ACTIVE: 0, UPCOMING: 1, EXPIRING: 2, STOPPED: 3 };
const statusRank = ticket => STATUS_PRIORITY[ticket?.labelStatus] ?? 99;

/**
 * 회원목록 "수강권 정보" 셀 (시안 1a: 풀 라벨 인라인).
 * 상세 페이지에서도 재사용할 수 있도록 순수 표시용 컴포넌트로 분리.
 *
 * @param {Array} tickets - 회원의 수강권/이용권/체험권 목록 (undefined/[] 허용)
 * @param {number|string} currentBranchId - 지금 보고 있는 지점(호출부 호환용. 칩 제거 후 셀 안에서는 안 씀).
 */
const TicketInfoCell = ({ tickets, currentBranchId }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const list = Array.isArray(tickets) ? tickets : [];
  if (list.length === 0) return null;

  // 만료(EXPIRED)는 목록에서 완전히 제외한다(옛 ticketName 정책과 동일).
  // 남는 것은 활성 우선순위로 정렬해 활성 상품이 먼저 노출되게 한다(동순위는 기존 순서 유지 — 안정 정렬).
  const visibleTickets = list
    .filter(ticket => ticket?.labelStatus !== 'EXPIRED')
    .sort((a, b) => statusRank(a) - statusRank(b));

  // MAX_VISIBLE_TICKETS 초과분만 접는다.
  const shownTickets = visibleTickets.slice(0, MAX_VISIBLE_TICKETS);
  const hiddenTickets = visibleTickets.slice(MAX_VISIBLE_TICKETS);
  const hiddenCount = hiddenTickets.length;

  const buildMeta = ticket => {
    const parts = [];
    if (ticket.usagePeriod != null && ticket.usagePeriodType) {
      parts.push(
        ticket.usagePeriodType === 'MONTH' ? `${ticket.usagePeriod}${t('MONTH')}` : `${ticket.usagePeriod}${t('DAY')}`,
      );
    }
    if (ticket.ticketKind !== 'PASS') {
      if (ticket.unlimited) {
        parts.push(t('Unlimited'));
      } else if (ticket.usageCount != null) {
        parts.push(`${ticket.usageCount}${t('LESSON')}`);
      }
    }
    let meta = parts.join(' ');
    if (ticket.trainerName) {
      meta = meta ? `${meta} / ${ticket.trainerName}` : ticket.trainerName;
    }
    return meta;
  };

  const toggleExpanded = e => {
    e.stopPropagation();
    setExpanded(prev => !prev);
  };

  const renderLine = ticket => {
    const statusChip = STATUS_CHIP_MAP[ticket.labelStatus] || STATUS_CHIP_MAP.EXPIRED;
    const meta = buildMeta(ticket);
    const period = formatPeriod(ticket.startDate, ticket.endDate);
    const isExpiring = ticket.labelStatus === 'EXPIRING';
    const isStopped = ticket.labelStatus === 'STOPPED';
    const isExpired = ticket.labelStatus === 'EXPIRED';
    const key = ticket.memberProductId || `${ticket.labelStatus}-${ticket.productName}-${ticket.startDate}`;

    return (
      <TicketLine key={key} dimmed={isExpired}>
        <StatusChip fg={statusChip.fg}>{t(statusChip.label)}</StatusChip>
        {/* 판매지점 칩(#73)은 2026-09-04 사용자 결정으로 제거 */}
        <ProductName>{ticket.productName}</ProductName>
        {meta && <MetaText>[{meta}]</MetaText>}
        {period && (
          <PeriodGroup>
            <PeriodText>{period}</PeriodText>
            {isStopped && ticket.stoppedEndDate && (
              <StoppedNote>{t('~ [중지 {{date}}까지]', { date: ticket.stoppedEndDate })}</StoppedNote>
            )}
            {isExpiring && ticket.dday != null && <DdayText>D-{ticket.dday}</DdayText>}
          </PeriodGroup>
        )}
      </TicketLine>
    );
  };

  return (
    <CellRoot>
      {shownTickets.map(renderLine)}
      {expanded && hiddenTickets.map(renderLine)}
      {hiddenCount > 0 && (
        <ExpiredToggle
          role="button"
          tabIndex={0}
          onClick={toggleExpanded}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleExpanded(e);
            }
          }}>
          <span>{expanded ? t('접기') : t('외 {{count}}건 더보기', { count: hiddenCount })}</span>
          <ToggleChevron>{expanded ? '▲' : '▼'}</ToggleChevron>
        </ExpiredToggle>
      )}
    </CellRoot>
  );
};

export default TicketInfoCell;
